if (!customElements.get('quick-cart-drawer')) {
  class QuickCartDrawer extends HTMLElement {
    constructor() {
      super();
      if (Shopify.designMode) {
        window.addEventListener('shopify:section:load', this.init.bind(this));
        this.parentElement.addEventListener('shopify:section:select', () => this.open());
        this.parentElement.addEventListener('shopify:section:deselect', () => this.close());
      }
    }

    connectedCallback() {
      this.init();
    }

    init() {
      this.toggleState = false;

      if (!this.classList.contains('is--open')) {
        document.querySelector('body').classList.remove('overflow-hidden');
      }

      this.querySelector('.button--close').addEventListener('click', this.close.bind(this));
      this.querySelector('.quick-cart-drawer__backdrop').addEventListener('click', this.close.bind(this));

      this.querySelector('.quick-cart-drawer__blocks').addEventListener('keydown', e => {
        if (e.key === 'Escape' && this.toggleState) {
          this.close();
        }

        if (this.toggleState) {
          const focusableElements = this.querySelectorAll(
            'button, [href], input, select, label, textarea, [tabindex]:not([tabindex="-1"])'
          );
          const firstFocusableElement = focusableElements[0];
          const lastFocusableElement = focusableElements[focusableElements.length - 1];

          let isTabPressed = e.key === 'Tab';

          if (!isTabPressed) {
            return;
          }

          if (e.shiftKey) {
            if (document.activeElement === firstFocusableElement) {
              e.preventDefault();
              lastFocusableElement.focus();
            }
          } else {
            if (document.activeElement === lastFocusableElement) {
              e.preventDefault();
              firstFocusableElement.focus();
            }
          }
        }
      });

      this.initTriggers();
    }

    initTriggers() {
      const observer = new MutationObserver(() => {
        document.querySelectorAll('.quick-cart-drawer__trigger').forEach(element => {
          if (!element._init) {
            element.addEventListener('click', event => {
              this.fetchProductForQuickCartDrawer(event);
            });
            element._init = true;
          }
        });
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }

    syncDrawerOptions(productOptions, fetchedProductRoot) {
      const drawerProductCard = this.querySelector('product-card');
      if (!drawerProductCard || !productOptions?.length) {
        drawerProductCard?.init();
        return;
      }

      productOptions.forEach(productOption => {
        let productOptionName = productOption.name;
        const optionSectionIdAttribute = productOption.dataset?.sectionId;

        if (optionSectionIdAttribute) {
          productOptionName = productOptionName.replace(optionSectionIdAttribute, '');
        }

        const quickCartOptionName = `${productOptionName}-quick-cart-product`;
        const quickCartDrawerOption = this.querySelector(
          `[name="${CSS.escape(quickCartOptionName)}"][value="${CSS.escape(productOption.value)}"]`
        );

        if (!quickCartDrawerOption) return;

        const quickCartDrawerOptionLabel = quickCartDrawerOption
          .closest('.js-product-card-options')
          ?.querySelector('legend');

        this.querySelectorAll(`[name="${CSS.escape(quickCartOptionName)}"]`).forEach(radioInput => {
          radioInput.removeAttribute('checked');
          radioInput.checked = false;
          radioInput.closest('li')?.classList.remove('checked');
        });

        quickCartDrawerOption.setAttribute('checked', '');
        quickCartDrawerOption.checked = true;
        quickCartDrawerOption.closest('li')?.classList.add('checked');

        const quickCartDrawerOptionLabelSpan = quickCartDrawerOptionLabel?.querySelector('[data-selected-variant]');
        if (quickCartDrawerOptionLabelSpan) {
          quickCartDrawerOptionLabelSpan.textContent = productOption.value;
        }
      });

      const variantIdInput = drawerProductCard.querySelector('input[name="id"]');
      const fetchedVariantIdInput = fetchedProductRoot?.querySelector('input[name="id"]');
      if (variantIdInput && fetchedVariantIdInput) {
        variantIdInput.value = fetchedVariantIdInput.value;
        variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      drawerProductCard.init();
    }

    async fetchProductForQuickCartDrawer(event) {
      if (event.target.closest('card-product-slider')) {
        event.target.closest('card-product-slider').slider.autoplay.stop();
        event.target.closest('card-product-slider').classList.add('product--open-on-quick-cart');
      }

      const isRecommendations = event.target.classList.contains('quick-cart-drawer__trigger--recommendations');
      const sourceProductCard = event.target.closest('product-card');
      let productOptions = [];

      if (!isRecommendations) {
        if (!sourceProductCard) return;
        productOptions = Array.from(
          sourceProductCard.querySelectorAll('input[type="radio"]:checked, select')
        );
      }

      const trigger = event.target.closest('[data-product-url]');
      if (!trigger) return;

      trigger.classList.toggle('is--loading');
      let fetchedProductRoot = null;

      try {
        let fetchPrefix = '';
        if (window.Shopify.routes.root.includes(`/${window.Shopify.locale}/`)) {
          fetchPrefix = `/${window.Shopify.locale}`;
        }

        const productCardResponse = await fetch(`${fetchPrefix}${trigger.dataset.productUrl}/?view=quick-cart`);
        if (!productCardResponse) return;

        const productCardHTML = await productCardResponse.text();
        const fetchedProductContainer = document.createElement('DIV');
        fetchedProductContainer.insertAdjacentHTML('beforeend', productCardHTML);
        fetchedProductRoot = fetchedProductContainer.querySelector('.quick-cart-product');
        if (!fetchedProductRoot) return;

        this.querySelector('.quick-cart-drawer__main').innerHTML = '';
        this.querySelector('.quick-cart-drawer__main').append(fetchedProductRoot);

        this.sliderInstance = new Swiper('.quick-cart-drawer__media-swiper', {
          slidesPerView: 1.25,
          spaceBetween: 8,
          freeMode: {
            enabled: true
          },
          breakpoints: {
            750: {
              slidesPerView: 2
            }
          }
        });
      } catch (error) {
        console.error(error);
      } finally {
        trigger.classList.toggle('is--loading');

        if (!isRecommendations) {
          this.syncDrawerOptions(productOptions, fetchedProductRoot);
        } else {
          this.querySelector('product-card')?.init();
        }

        setTimeout(() => {
          this.open();
        }, 500);
      }
    }

    setActiveMedia(id) {
      const mediaFound = Array.from(this.querySelectorAll('[data-media-id]')).find(
        media => Number(media.dataset.mediaId) === id
      );
      if (mediaFound && this.sliderInstance) {
        this.sliderInstance.slideTo(Number(mediaFound.dataset.index));
      }
    }

    toggle() {
      if (!this.toggleState) {
        this.open();
      } else {
        this.close();
      }
    }

    open() {
      this.toggleState = true;
      document.querySelector('body').classList.add('overflow-hidden');
      const quickCartDrawer = document.querySelector('.quick-cart-drawer__blocks');
      const closeButton = quickCartDrawer.querySelector('.button--close');
      quickCartDrawer.setAttribute('tabindex', '0');
      closeButton.setAttribute('tabindex', '0');
      this.classList.add('is--open');
      this.opened();

      const firstFocusableElement = this.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (firstFocusableElement) {
        firstFocusableElement.focus();
      }
    }

    close() {
      this.toggleState = false;

      const shopTheLookDrawer = document.querySelector('shop-the-look-drawer');

      if (shopTheLookDrawer && !shopTheLookDrawer.classList.contains('is--open')) {
        document.querySelector('body').classList.remove('overflow-hidden');
      }
      if (!shopTheLookDrawer) {
        document.querySelector('body').classList.remove('overflow-hidden');
      }
      this.classList.remove('is--open');
      this.closed();
      this.toggleAriaExpanded();

      if (document.querySelector('card-product-slider.product--open-on-quick-cart')) {
        document.querySelector('card-product-slider.product--open-on-quick-cart').slider.autoplay.start();
        document.querySelector('card-product-slider.product--open-on-quick-cart').classList.remove('product--open-on-quick-cart');
      }
    }

    toggleAriaExpanded(event) {
      if (event) {
        if (event.target.closest('button')) event.target.closest('button').setAttribute('aria-expanded', true);
        this.querySelector('.button--close').setAttribute('aria-expanded', true);
      } else {
        document.querySelectorAll('[aria-controls="quick-cart-drawer"]').forEach(button => {
          button.setAttribute('aria-expanded', false);
        });
      }
    }

    opened() {
      const openedEvent = new Event('opened', { bubbles: true });
      this.dispatchEvent(openedEvent);
    }

    closed() {
      const closedEvent = new Event('closed', { bubbles: true });
      this.dispatchEvent(closedEvent);
    }
  }

  customElements.define('quick-cart-drawer', QuickCartDrawer);
}
